/**
 * LGPD Block E — optional frontend Sentry readiness (disabled by default).
 *
 * `initSentry()` is a no-op unless `VITE_SENTRY_ENABLED=true`, a DSN is set, and
 * (when `VITE_SENTRY_REQUIRE_CONSENT` is on, the default) analytics consent has
 * been granted. Even then, `@sentry/browser` is an OPTIONAL dependency loaded at
 * runtime; if it isn't bundled/available the readiness stays dormant.
 *
 * When it does initialize, it disables default PII, installs the `beforeSend`
 * scrubber, and never enables Session Replay (must stay off in this block).
 */

import { OBSERVABILITY_ENV } from '@/shared/config/env'
import { hasAnalyticsConsent } from '@/shared/consent'
import { scrubSentryEvent, sentryShouldInit } from './sentryPolicy'

let initialized = false

/** Initialize Sentry IF allowed. Returns true only when actually initialized. */
export async function initSentry(): Promise<boolean> {
  if (initialized) return true
  if (typeof window === 'undefined') return false

  const cfg = OBSERVABILITY_ENV.sentry
  const allowed = sentryShouldInit(
    { enabled: cfg.enabled, dsn: cfg.dsn, requireConsent: cfg.requireConsent },
    hasAnalyticsConsent(),
  )
  if (!allowed) return false

  try {
    // Optional dependency, resolved at runtime. Vite leaves this import alone
    // (variable specifier + @vite-ignore); activating frontend Sentry means
    // installing/bundling `@sentry/browser` (operator step, Block E2-adjacent).
    const moduleName = '@sentry/browser'
    const Sentry = (await import(/* @vite-ignore */ moduleName)) as {
      init: (opts: Record<string, unknown>) => void
    }
    Sentry.init({
      dsn: cfg.dsn,
      environment: cfg.environment || undefined,
      tracesSampleRate: cfg.tracesSampleRate,
      sendDefaultPii: false,
      // Session Replay intentionally NOT configured/enabled in this block.
      beforeSend: (event: unknown) => scrubSentryEvent(event),
    })
    initialized = true
    return true
  } catch {
    // SDK not installed/bundled or init failed → stay dormant, never throw.
    return false
  }
}
